import React from 'react';
import { Box, Card, CardContent, Chip, styled, Typography } from '@mui/material';
import { AppMode, useAppMode, useSelectedCommentDecisionQuery, useSelectedComment } from '@tymely/services';
import { IActionExecution } from '@tymely/atoms';
import { TimelineList } from '@tymely/components';

import IntentActions from './IntentActions';

const _Root = styled(Card)`
    overflow: unset;
    border-radius: 0;
    padding: ${({ theme }) => theme.spacing(1)};
    background: ${({ theme }) => theme.palette.secondary.main};
    .MuiTypography-root {
        color: ${({ theme }) => theme.palette.background.paper};
    }
    .MuiSvgIcon-root {
        color: ${({ theme }) => theme.palette.background.paper};
    }
`;

const _CardContent = styled(CardContent)`
    padding: ${({ theme }) => theme.spacing(0, 0)};
    padding-bottom: 0 !important;
`;

const _Label = styled(Typography)`
    margin: ${({ theme }) => theme.spacing(1, 0)};
`;

const _DecisionLabel = styled(Typography)`
    display: inline;
    margin-right: ${({ theme }) => theme.spacing(1)};
`;

const DecisionChip = styled(Chip, { shouldForwardProp: (prop: string) => !['isLive'].includes(prop) })<{
    isLive: boolean;
}>`
    margin-top: ${({ theme }) => theme.spacing(0.5)};
    font-size: 0.8rem;
    background-color: ${({ isLive, theme }) => (isLive ? theme.palette.primary.main : theme.palette.warning.main)};
    border: none;
    color: #fff;
`;

const prepareActions = (actions: IActionExecution[]) => {
    return actions
        .sort((a, b) => a.order - b.order)
        .map((action) => `${action.action_metadata.title} (${action.status})`);
};

type DecisionProps = {
    version?: string;
};

const Decision = ({ version }: DecisionProps) => {
    const decisionQuery = useSelectedCommentDecisionQuery(!version);
    const { appMode } = useAppMode();
    const comment = useSelectedComment();
    const decision = decisionQuery.data;

    const titles = React.useMemo(() => {
        if (!decision?.action_executions) {
            return [];
        }
        return prepareActions(decision?.action_executions);
    }, [decision]);

    const isIntentApproved =
        appMode !== AppMode.Online || !!comment?.agent_username || !!comment?.additional_data.is_intent_approved;

    if (!decision || !isIntentApproved) {
        return null;
    }

    return (
        <_Root>
            <_CardContent>
                <Box sx={{ p: 1 }}>
                    <IntentActions version={version} />
                    <Box
                        sx={{
                            mt: 2,
                            mb: 1,
                            display: 'flex',
                            justifyItems: 'center',
                        }}
                    >
                        <Typography variant="h6" sx={{ display: 'inline', mr: 1 }}>
                            Decision ({decision.execution_status})
                        </Typography>
                        <DecisionChip
                            label={decision.status}
                            size="small"
                            isLive={decision.status === 'LIVE'}
                            variant="filled"
                        />
                    </Box>
                    <_Label>Workflow {decision.workflow_id}:</_Label>
                    <Box sx={{ height: 8 }} />
                    <_Label>{decision.title || 'No decision'}</_Label>
                    {!!titles.length && <TimelineList id="comment-actions" items={titles} />}
                </Box>
            </_CardContent>
        </_Root>
    );
};

export default Decision;
